import React, { useEffect, useContext, useRef } from 'react';
import videojs from 'video.js';
import '@silvermine/videojs-airplay/dist/silvermine-videojs-airplay.css';
import '@silvermine/videojs-chromecast/dist/silvermine-videojs-chromecast.css';
import '@videojs/http-streaming';
import 'video.js/dist/video-js.css';
import './PinkPlay/index.css';
import { AiOutlineClose } from "react-icons/ai";
import { VideoPlayerContext } from './VideoPlayerContext';
import { getGlobalAudio } from './Player';

// Import plugins
require('@silvermine/videojs-chromecast')(videojs, { preloadWebComponents: true });
require('@silvermine/videojs-airplay')(videojs);
require('@silvermine/videojs-quality-selector')(videojs);
require('@silvermine/videojs-quality-selector/dist/css/quality-selector.css');

const defaultSources = [
  { src: 'https://deams.xseu.net/MegaHit/index.m3u8', type: 'application/x-mpegURL', label: '1080P' },
  { src: 'https://deams.xseu.net/MegaHit_720p/index.m3u8', type: 'application/x-mpegURL', label: '720P' },
  { src: 'https://deams.xseu.net/MegaHit_360p/index.m3u8', type: 'application/x-mpegURL', label: '360P' },
];

const GlobalVideoPlayer = () => {
  const streamLoadedRef = useRef(false);
  const { 
    isAttached, 
    isClosed, 
    setIsClosed, 
    isPlaying, 
    setIsPlaying, 
    playerRef, 
    defaultContainerRef, 
    playerUI 
  } = useContext(VideoPlayerContext);

  const attachStream = (player) => {
    if (!player || streamLoadedRef.current) {
        return;
    }

    player.src(defaultSources);
    player.load();
    streamLoadedRef.current = true;
  };

  const stopStream = (player) => {
    if (!player || !streamLoadedRef.current) {
        return;
    }

    streamLoadedRef.current = false;
    player.pause();
    player.reset();
    setIsPlaying(false);
  };

  useEffect(() => {
    // 1. Ensure the playerUI is inside the default container initially (or if detached)
    if (!isAttached && defaultContainerRef.current && playerUI) {
        defaultContainerRef.current.appendChild(playerUI);
    }

    // 2. Initialize VideoJS if not done
    if (!playerRef.current && playerUI) {
        if (playerUI.children.length === 0) {
            const videoWrapper = document.createElement('div');
            videoWrapper.setAttribute('data-vjs-player', 'true');
            // videoWrapper.style.width = '100%';
            // videoWrapper.style.height = '100%';

            const videoElement = document.createElement('video');
            videoElement.className = 'video-js vjs-theme-forest';
            videoElement.preload = 'none';
            videoElement.oncontextmenu = (e) => e.preventDefault();
            
            videoWrapper.appendChild(videoElement);
            playerUI.appendChild(videoWrapper);

            const videoJsOptions = {
                autoplay: false,
                preload: 'none',
                controlBar: {
                  children: [
                     'playToggle',
                     'progressControl',
                     'volumePanel',
                     'qualitySelector',
                     'fullscreenToggle',
                  ],
               },
                plugins: {
                  airPlay: { addButtonToControlBar: true },
                  chromecast: {},
               },
                controls: true,
                fluid: true,
                muted: false,
                responsive: true,
                experimentalLLHLS: true,
                sources: []
              };
    
            const player = videojs(videoElement, videoJsOptions, () => {
                console.log('Global Player Ready');
            });
    
            playerRef.current = player;

            const originalPlay = player.play.bind(player);
            player.play = (...args) => {
                attachStream(player);
                const playResult = originalPlay(...args);
                if (playResult && typeof playResult.catch === 'function') {
                    return playResult.catch((error) => {
                        stopStream(player);
                        throw error;
                    });
                }
                return playResult;
            };
            
            player.on('play', () => {
                console.log('GlobalPlayer: Play event');
                setIsPlaying(true);
                setIsClosed(false);
                const globalAudio = getGlobalAudio();
                if (globalAudio) {
                    globalAudio.pause();
                }
            });
            player.on('pause', () => {
                console.log('GlobalPlayer: Pause event');
                setIsPlaying(false);
                stopStream(player);
            });
            player.on('error', (e) => console.error('GlobalPlayer: Error', e));
        }
    }

  }, []); 

  // Effect to handle moving the player back when detached
  useEffect(() => {
    console.log(`GlobalPlayer: isAttached changed to ${isAttached}`);
    if (!isAttached && defaultContainerRef.current && playerUI) {
        defaultContainerRef.current.appendChild(playerUI);
        
        // Safety check: if player is actually playing but state says otherwise
        if (playerRef.current && !playerRef.current.paused() && !isPlaying) {
             console.log('GlobalPlayer: Force setting isPlaying to true');
             setIsPlaying(true);
        }
    }
  }, [isAttached, playerUI]);


  const handleClose = () => {
    if (playerRef.current) {
        stopStream(playerRef.current);
    }
    setIsPlaying(false);
    setIsClosed(true);
  };

  const shouldShow = !isAttached && isPlaying && !isClosed;
  console.log('GlobalPlayer Render: ', { isAttached, isPlaying, isClosed, shouldShow });

  const floatingStyle = {
    position: 'fixed',
    bottom: '20px',
    right: '20px',
    width: '320px',
    height: '180px',
    zIndex: 9999,
    boxShadow: '0 4px 12px rgba(0,0,0,0.5)',
    display: shouldShow ? 'block' : 'none',
    backgroundColor: '#000'
  };

  return (
    <div 
        ref={defaultContainerRef} 
        style={floatingStyle}
        className="global-player-floating-container"
    >
        {/* Close Button */}
        {!isAttached && (
            <div 
                onClick={handleClose} 
                style={{
                    position: 'absolute', 
                    top: '-15px', 
                    right: '-15px', 
                    background: 'red', 
                    borderRadius: '50%', 
                    width: '30px', 
                    height: '30px', 
                    display: 'flex', 
                    justifyContent: 'center', 
                    alignItems: 'center', 
                    cursor: 'pointer',
                    zIndex: 10000,
                    color: 'white'
                }}
            >
                <AiOutlineClose />
            </div>
        )}
        {/* Content is appended manually */}
    </div>
  );
};

export default GlobalVideoPlayer;
